import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/ui";
import { cn } from "@/lib/cn";
import { builder } from "@/data/services";
import type { ComponentNode, ComponentTree } from "@/editor/types";

/**
 * Version history + restore (capability #17). Lists a page's versions newest
 * first with a lightweight diff (block-count delta vs the current version) and a
 * one-click Restore that copies an old version's tree into a new draft
 * (non-destructive) via builder-api, then reloads the editor onto it.
 */
function countNodes(tree: ComponentTree | undefined): number {
  if (!tree?.root) return 0;
  const walk = (n: ComponentNode): number => 1 + (n.children ?? []).reduce((s, c) => s + walk(c), 0);
  return walk(tree.root);
}

function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function VersionHistory({
  pageId,
  currentVersionId,
  onRestored,
}: {
  pageId: string;
  currentVersionId: string | undefined;
  /** Called after a successful restore so the host can reload the tree. */
  onRestored: () => void;
}) {
  const qc = useQueryClient();
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["versions", pageId],
    queryFn: () => builder.listVersions(pageId),
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => builder.restoreVersion(pageId, versionId),
    onMutate: (versionId) => setRestoringId(versionId),
    onSettled: () => setRestoringId(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["versions", pageId] });
      qc.invalidateQueries({ queryKey: ["page", pageId] });
      onRestored();
    },
  });

  const items = q.data?.items ?? [];
  // Block count of the live/current version, for the diff readout.
  const current = items.find((v) => v.id === currentVersionId);
  const currentCount = countNodes(current?.tree);

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 px-1 py-8 text-[13px] text-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
      </div>
    );
  }
  if (q.isError) {
    return <p className="px-1 py-8 text-[13px] text-accent">Couldn’t load version history.</p>;
  }
  if (items.length === 0) {
    return (
      <div className="grid place-items-center px-4 py-12 text-center">
        <History className="h-6 w-6 text-subtle" strokeWidth={1.5} />
        <p className="mt-2 text-[14px] font-medium text-ink-strong">No versions yet</p>
        <p className="mt-1 text-[13px] text-muted">Saved drafts and publishes will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {restore.isError && (
        <p className="text-[12px] text-accent">
          {restore.error instanceof Error ? restore.error.message : "Restore failed."}
        </p>
      )}
      {items.map((v) => {
        const isCurrent = v.id === currentVersionId;
        const count = countNodes(v.tree);
        const delta = count - currentCount;
        return (
          <div
            key={v.id}
            className={cn(
              "flex items-start gap-3 rounded-card border p-3",
              isCurrent ? "border-accent/40 bg-accent-soft/30" : "border-hairline bg-paper dark:bg-white/[0.03]",
            )}
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-semibold text-ink-strong">v{v.version_n}</span>
                <span className="rounded-chip bg-paper-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-subtle dark:bg-white/5">
                  {v.status}
                </span>
                {isCurrent && (
                  <span className="rounded-chip bg-accent-soft px-1.5 py-0.5 text-[10px] font-medium text-accent">
                    current
                  </span>
                )}
              </div>
              {v.message && <p className="mt-1 truncate text-[12.5px] text-muted">{v.message}</p>}
              <p className="mt-0.5 text-[11px] text-subtle">
                {ago(v.created_at)} · {count} {count === 1 ? "block" : "blocks"}
                {!isCurrent && delta !== 0 && (
                  <span className={delta > 0 ? "text-emerald-600" : "text-accent"}>
                    {" "}
                    ({delta > 0 ? "+" : ""}
                    {delta} vs current)
                  </span>
                )}
              </p>
            </div>
            {!isCurrent && (
              <Button
                variant="ghost"
                size="sm"
                disabled={restore.isPending}
                onClick={() => restore.mutate(v.id)}
              >
                {restoringId === v.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
                )}
                Restore
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
